<svelte:options customElement="hb-messages-list" />

<script lang="ts">
  import type { Component, TAuthor } from "./types/webcomponent.type";

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  // Default options
  const defaultOptions = {
    showTimestamp: true,
    showAvatar: true,
    showName: true,
    bubbles: false,
  };

  let {
    id = "",
    // style = "",
    messages,
    authors,
    options = defaultOptions,
    actions = [],
  }: Component = $props();

  type Message = Component["messages"][0] & {
    author: Component["authors"][0];
    dateString: string;
    showAvatar: boolean;
    isConsecutive: boolean;
  };

  // Helper function to get default avatar
  function getDefaultAvatar(isAI: boolean = false): string {
    if (isAI) {
      return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzYzNjZGMCIvPjxwYXRoIGQ9Ik0yMCAxMEMxNS41OCAxMCAxMiAxMy41OCAxMiAxOEMxMiAyMi40MiAxNS41OCAyNiAyMCAyNkMyNC40MiAyNiAyOCAyMi40MiAyOCAxOEMyOCAxMy41OCAyNC40MiAxMCAyMCAxMFpNMjAgMjRDMTYuNjkgMjQgMTQgMjEuMzEgMTQgMThDMTQgMTQuNjkgMTYuNjkgMTIgMjAgMTJDMjMuMzEgMTIgMjYgMTQuNjkgMjYgMThDMjYgMjEuMzEgMjMuMzEgMjQgMjAgMjRaIiBmaWxsPSJ3aGl0ZSIvPjxwYXRoIGQ9Ik0xNiAxNkgxNlYxOEgxNlYxNloiIGZpbGw9IndoaXRlIi8+PHBhdGggZD0iTTI0IDE2SDI0VjE4SDI0VjE2WiIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJNMTggMjBIMjJWMjJIMThWMjBaIiBmaWxsPSJ3aGl0ZSIvPjwvc3ZnPg==";
    }
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzZDNzU3NyIvPjxwYXRoIGQ9Ik0yMCAxMEMxNS41OCAxMCAxMiAxMy41OCAxMiAxOEMxMiAyMi40MiAxNS41OCAyNiAyMCAyNkMyNC40MiAyNiAyOCAyMi40MiAyOCAxOEMyOCAxMy41OCAyNC40MiAxMCAyMCAxMFpNMjAgMjRDMTYuNjkgMjQgMTQgMjEuMzEgMTQgMThDMTQgMTQuNjkgMTYuNjkgMTIgMjAgMTJDMjMuMzEgMTIgMjYgMTQuNjkgMjYgMThDMjYgMjEuMzEgMjMuMzEgMjQgMjAgMjRaIiBmaWxsPSJ3aGl0ZSIvPjxwYXRoIGQ9Ik0xNiAxNkgxNlYxOEgxNlYxNloiIGZpbGw9IndoaXRlIi8+PHBhdGggZD0iTTI0IDE2SDI0VjE4SDI0VjE2WiIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJNMTggMjBIMjJWMjJIMThWMjBaIiBmaWxsPSJ3aGl0ZSIvPjwvc3ZnPg==";
  }

  // Helper function to copy text to clipboard
  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log("Message copied to clipboard");
    });
  }

  let msgs: Message[] = $derived.by(() => {
    if (!messages || !authors) return [];

    const processedMessages =
      messages
        ?.filter?.(
          (f) =>
            authors?.find?.((a) => a?.id && f?.authorId && a.id === f.authorId)
              ?.id,
        )
        ?.map?.((message, index) => {
          const author = authors?.find?.(
            (a) => a.id === message.authorId,
          ) as TAuthor;
          const isToday =
            new Date(message.timestamp).toDateString() ===
            new Date().toDateString();
          const messageTimestamp = new Date(message.timestamp);
          const hourAndMinutes =
            messageTimestamp.toLocaleTimeString().split(":")[0] +
            ":" +
            messageTimestamp.toLocaleTimeString().split(":")[1];
          const dateAndHourAndMinutes =
            messageTimestamp.toLocaleString().split(" ")[0] +
            " " +
            messageTimestamp.toLocaleTimeString().split(":")[0] +
            ":" +
            messageTimestamp.toLocaleTimeString().split(":")[1];
          const dateString = isToday ? hourAndMinutes : dateAndHourAndMinutes;

          // Check if this is a consecutive message from the same author
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const isConsecutive = Boolean(
            previousMessage &&
            previousMessage.authorId === message.authorId &&
            new Date(message.timestamp).getTime() -
              new Date(previousMessage.timestamp).getTime() <
              300000, // 5 minutes
          );

          // Show avatar if not consecutive or if it's the first message, and if showAvatar option is enabled
          const showAvatar =
            options.showAvatar && (!isConsecutive || index === 0)
              ? true
              : false;

          return {
            ...message,
            author,
            dateString,
            showAvatar,
            isConsecutive,
          };
        }) || [];

    return processedMessages;
  });
  //  let componentStyleToSet: string = "";

  $effect(() => {
    if (typeof messages === "string") {
      try {
        messages = JSON.parse(messages);
      } catch (err) {
        console.error("error parsing messages", err);
      }
    }
    if (typeof actions === "string") {
      try {
        actions = JSON.parse(actions);
      } catch (err) {
        console.error("error parsing actions", err);
      }
    }
  });
  $effect(() => {
    if (typeof options === "string") {
      try {
        options = JSON.parse(options);
        // Merge with defaults
        options = { ...defaultOptions, ...options };
      } catch (err) {
        console.error("error parsing options", err);
      }
    }
  });
  $effect(() => {
    if (typeof authors === "string") {
      try {
        authors = JSON.parse(authors);
      } catch (err) {
        console.error("error parsing authors", err);
      }
    }
  });
</script>

<div class="hb-messages-list-root">
  {#if msgs.length === 0}
    <div class="hb-messages-list-empty" part="empty-state">
      <i class="bi bi-chat-dots hb-messages-list-empty-icon" aria-hidden="true"></i>
      <p class="hb-messages-list-empty-text">No messages yet.</p>
    </div>
  {:else}
    <div class="hb-messages-list-feed" role="list" aria-label="Messages">
{#each msgs as message (message.id)}
  <article class="hb-messages-list-item" role="listitem">
  <!-- Author name positioned above message content -->
  {#if options.showName}
    <div
      class="author-name-wrapper is-flex is-align-items-center mb-2 {message
        .author.me
        ? 'is-justify-content-flex-end'
        : 'is-justify-content-flex-start'}"
    >
      {#if !message.author.me && options.showAvatar}
        <div class="author-name-spacer mr-2"></div>
      {/if}
      <div class="author-name-content is-flex-grow-1">
        <span
          class="has-text-weight-semibold {message.author.me
            ? 'has-text-secondary'
            : 'has-text-primary'}"
        >
          {message.author.me ? "You" : message.author.name}
        </span>
      </div>
      {#if message.author.me && options.showAvatar}
        <div class="author-name-spacer ml-2"></div>
      {/if}
    </div>
  {/if}

  <div
    class="message-wrapper is-flex mb-4 {message.author.me
      ? 'is-justify-content-flex-end'
      : 'is-justify-content-flex-start'}"
  >
    {#if !message.author.me && options.showAvatar}
      <div class="message-avatar mr-2">
        {#if message.showAvatar}
          <div class="avatar-container is-position-relative">
            <img
              src={message.author.avatar ||
                getDefaultAvatar(message.author.isAI)}
              alt={message.author.name}
              class="has-radius-rounded"
              width="40"
              height="40"
            />
            {#if message.author.isAI}
              <span class="tag is-primary is-rounded hb-messages-ai-badge">
                AI
              </span>
            {/if}
          </div>
        {:else}
          <div class="avatar-spacer"></div>
        {/if}
      </div>
    {/if}

    <div class="message-content is-position-relative">
      {#if options.bubbles}
        <div
          part="message-bubble-{message.author.me ? 'me' : 'them'}"
          class="message-bubble px-4 py-4 {message.author.me
            ? 'message-bubble--self'
            : 'message-bubble--peer'}"
        >
          <div part="message-body" class="message-body">
            {#if message.type === "code"}
              <div class="code-block">
                {#if message.language}
                  <div
                    class="code-header is-flex is-justify-content-space-between is-align-items-center mb-2"
                  >
                    <span class="tag is-secondary">{message.language}</span>
                  </div>
                {/if}
                <pre class="hb-code-pre p-3 mb-0 has-radius-small"><code
                    >{message.text}</code
                  ></pre>
              </div>
            {:else}
              <div class="message-text">{message.text}</div>
            {/if}
          </div>
        </div>
      {:else}
        <div
          part="message"
          class="message-bubble message-bubble--neutral px-4 py-4"
        >
          <div part="message-body" class="message-body">
            {#if message.type === "code"}
              <div class="code-block">
                {#if message.language}
                  <div
                    class="code-header is-flex is-justify-content-space-between is-align-items-center mb-2"
                  >
                    <span class="tag is-secondary">{message.language}</span>
                  </div>
                {/if}
                <pre class="hb-code-pre p-3 mb-0 has-radius-small"><code
                    >{message.text}</code
                  ></pre>
              </div>
            {:else}
              <div class="message-text">{message.text}</div>
            {/if}
          </div>
        </div>
      {/if}

      <!-- Action buttons -->
      {#if actions?.length}
        <div
          class="message-actions is-flex is-flex-wrap-wrap is-align-items-center is-gap-1 mt-2 {message
            .author.me
            ? 'is-justify-content-flex-end'
            : 'is-justify-content-flex-start'}"
        >
          {#each actions as action (action.name)}
            <button
              type="button"
              class="button is-small is-outlined action-icon-btn"
              onclick={() =>
                dispatch("action", {
                  messageId: message.id,
                  action: action.name,
                })}
              title={action.name}
              aria-label={action.name}
            >
              <i class="bi bi-{action.icon}"></i>
            </button>
          {/each}

          {#if options.showTimestamp}
            <div part="message-timestamp" class="message-timestamp ml-2">
              {message.dateString}
            </div>
          {/if}
        </div>
      {:else if options.showTimestamp}
        <div
          class="message-timestamp-only is-flex mt-2 {message.author.me
            ? 'is-justify-content-flex-end'
            : 'is-justify-content-flex-start'}"
        >
          <div part="message-timestamp" class="message-timestamp">
            {message.dateString}
          </div>
        </div>
      {/if}
    </div>

    {#if message.author.me && options.showAvatar}
      <div class="message-avatar ml-2">
        {#if message.showAvatar}
          <img
            src={message.author.avatar || getDefaultAvatar(false)}
            alt="You"
            class="has-radius-rounded"
            width="40"
            height="40"
          />
        {:else}
          <div class="avatar-spacer"></div>
        {/if}
      </div>
    {/if}
  </div>
  </article>
{/each}
    </div>
  {/if}
</div>

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
